import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Mail, Phone, MapPin, Building2, BadgeCheck, CalendarDays,
  Briefcase, Clock3, Landmark, ShieldCheck, Users2, Pencil,
} from 'lucide-react';
import { employeeAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { StatCard } from '../components/common/StatCard';
import { CardSkeleton, StatSkeletonGrid, EmptyState } from '../components/common/States';
import ProfileSection from '../components/ProfileSection';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';

const Field = ({ label, value }) => (
  <div className="min-w-0">
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="mt-0.5 text-sm font-medium text-foreground break-words">{value || '—'}</p>
  </div>
);

const SectionCard = ({ title, icon: Icon, children, cols = 4 }) => (
  <Card>
    <CardHeader className="border-b border-border/60 pb-3">
      <CardTitle className="flex items-center gap-2 text-sm font-semibold">
        {Icon && <Icon className="h-4 w-4 text-primary" />}
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent className={`grid gap-4 pt-4 sm:grid-cols-2 ${cols === 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'}`}>
      {children}
    </CardContent>
  </Card>
);

const initialsOf = (e) =>
  ((e?.first_name?.[0] || '') + (e?.last_name?.[0] || '')).toUpperCase() || 'E';

const tenureLabel = (doj) => {
  if (!doj) return null;
  const start = new Date(doj);
  if (Number.isNaN(start.getTime())) return null;
  const months = Math.max(0, Math.round((Date.now() - start.getTime()) / (30.44 * 86400000)));
  const y = Math.floor(months / 12);
  const m = months % 12;
  if (y === 0) return `${m} mo`;
  return `${y}y ${m}m`;
};

const AdminEmployeeProfilePage = ({ employeeId }) => {
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await employeeAPI.get(employeeId);
        setEmployee(res.data);
      } catch (error) {
        toast.error(getErrorMessage(error, 'Failed to load employee profile'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [employeeId]);

  const tenure = useMemo(() => tenureLabel(employee?.date_of_joining), [employee]);

  if (loading) {
    return (
      <div className="space-y-6">
        <CardSkeleton rows={2} />
        <StatSkeletonGrid count={4} />
        <CardSkeleton rows={5} />
      </div>
    );
  }

  if (!employee) {
    return (
      <Card>
        <CardContent className="pt-6">
          <EmptyState
            icon={Users2}
            title="Employee not found"
            description="We couldn’t load this employee profile."
            actionLabel="Back to Admin"
            onAction={() => navigate('/admin')}
          />
        </CardContent>
      </Card>
    );
  }

  const name = `${employee.first_name || ''} ${employee.middle_name ? `${employee.middle_name} ` : ''}${employee.last_name || ''}`.trim();

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Profile hero */}
      <Card className="overflow-hidden">
        <div className="h-20 bg-gradient-to-r from-primary via-primary to-primary-hover" />
        <CardContent className="pt-0">
          <div className="-mt-10 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div className="flex items-end gap-4 min-w-0">
              {employee.profile_photo_url ? (
                <img
                  src={employee.profile_photo_url}
                  alt={name}
                  className="h-20 w-20 shrink-0 rounded-2xl object-cover ring-4 ring-card shadow-md"
                />
              ) : (
                <div className="grid h-20 w-20 shrink-0 place-items-center rounded-2xl bg-primary text-2xl font-bold text-primary-foreground ring-4 ring-card shadow-md">
                  {initialsOf(employee)}
                </div>
              )}
              <div className="min-w-0 pb-1">
                <div className="flex items-center gap-2">
                  <h2 className="truncate text-xl font-bold text-foreground">{name || employee.employee_id}</h2>
                  <Badge variant={employee.is_active ? 'success' : 'secondary'}>
                    {employee.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                <p className="mt-0.5 truncate text-sm text-muted-foreground">
                  {[employee.designation, employee.department].filter(Boolean).join(' · ') || employee.employee_id}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  {employee.email && <span className="inline-flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{employee.email}</span>}
                  {employee.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{employee.phone}</span>}
                  {employee.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{employee.location}</span>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => navigate('/admin')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <Button onClick={() => navigate(`/admin/employees/${employee.employee_id}/edit`)}>
                <Pencil className="h-4 w-4 mr-2" />
                Edit
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPI row */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Tenure" value={tenure || '—'} icon={Clock3} tone="primary" animate={false} hint={employee.date_of_joining ? `Joined ${new Date(employee.date_of_joining).toLocaleDateString()}` : undefined} />
        <StatCard label="Employment Type" value={employee.employment_type || '—'} icon={Briefcase} tone="info" animate={false} hint={employee.work_mode || undefined} />
        <StatCard label="Department" value={employee.department || '—'} icon={Building2} tone="purple" animate={false} hint={employee.sub_department || undefined} />
        <StatCard label="Status" value={employee.employment_status || (employee.is_active ? 'Active' : 'Inactive')} icon={BadgeCheck} tone={employee.is_active ? 'success' : 'slate'} animate={false} hint={employee.probation_end_date ? `Probation ends ${employee.probation_end_date}` : undefined} />
      </div>

      {/* Job & organization */}
      <SectionCard title="Job & Organization" icon={Building2}>
        <Field label="Designation" value={employee.designation} />
        <Field label="Department" value={employee.department} />
        <Field label="Sub Department" value={employee.sub_department} />
        <Field label="Location" value={employee.location} />
        <Field label="Employment Status" value={employee.employment_status} />
        <Field label="Employment Type" value={employee.employment_type} />
        <Field label="Work Mode" value={employee.work_mode} />
        <Field label="Grade" value={employee.grade} />
        <Field label="Cost Center" value={employee.cost_center} />
        <Field label="Date Of Joining" value={employee.date_of_joining ? new Date(employee.date_of_joining).toLocaleDateString() : null} />
        <Field label="Probation End Date" value={employee.probation_end_date} />
      </SectionCard>

      {/* Personal */}
      <SectionCard title="Personal Details" icon={CalendarDays}>
        <Field label="Preferred Name" value={employee.preferred_name} />
        <Field label="Personal Email" value={employee.personal_email} />
        <Field label="Date Of Birth" value={employee.date_of_birth} />
        <Field label="Gender" value={employee.gender} />
        <Field label="Marital Status" value={employee.marital_status} />
        <Field label="Blood Group" value={employee.blood_group} />
        <Field label="Nationality" value={employee.nationality} />
        <Field label="Address" value={employee.address} />
      </SectionCard>

      {/* Reporting */}
      <SectionCard title="Reporting Hierarchy" icon={Users2} cols={3}>
        <Field label="Reporting Manager ID" value={employee.reporting_manager_id} />
        <Field label="Second Level Manager ID" value={employee.second_level_manager_id} />
      </SectionCard>

      {/* Statutory & bank */}
      <SectionCard title="Statutory & Bank" icon={Landmark}>
        <Field label="PAN" value={employee.pan_number} />
        <Field label="Aadhaar" value={employee.aadhaar_number} />
        <Field label="UAN" value={employee.uan_number} />
        <Field label="PF Number" value={employee.pf_number} />
        <Field label="ESI Number" value={employee.esi_number} />
        <Field label="Bank Name" value={employee.bank_name} />
        <Field label="Bank Account" value={employee.bank_account_number} />
        <Field label="IFSC" value={employee.ifsc_code} />
      </SectionCard>

      {/* Extended profile (documents, contacts, dependents, salary, requests) */}
      <Card>
        <CardHeader className="border-b border-border/60 pb-3">
          <CardTitle className="flex items-center gap-2 text-sm font-semibold">
            <ShieldCheck className="h-4 w-4 text-primary" />
            Extended Profile
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <ProfileSection employeeId={employee.employee_id} />
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminEmployeeProfilePage;
